import React, { useEffect, useRef } from 'react';
import { useField } from 'formik';
import { makeStyles } from '@material-ui/styles';
import { createStyles } from '@material-ui/core';
import { useIntl } from 'util/IntlMessages';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      note: {
        position: 'relative',
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.secondary.semiLight}`,
        borderRadius: 8,
        padding: '10px 12px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      },
      head: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.darkGrey.color,
        letterSpacing: '0.01em',
      },
      icn: {
        display: 'flex',
        color: theme.palette.primary.main,
      },
      title: {
        flex: 1,
        color: theme.palette.text.primary,
      },
      count: {
        fontWeight: 400,
        color: theme.palette.secondary.main,
        fontSize: 11,
        fontVariantNumeric: 'tabular-nums',
      },
      input: {
        width: '100%',
        resize: 'vertical',
        border: 0,
        background: 'transparent',
        padding: 0,
        fontFamily: 'inherit',
        fontSize: 13.5,
        lineHeight: 1.45,
        color: theme.palette.text.primary,
        outline: 0,
        '&::placeholder': {
          color: theme.palette.secondary.main,
        },
      },
      foot: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingTop: 4,
        borderTop: `1px dashed ${theme.palette.secondary.semiLight}`,
      },
      hint: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11.5,
        color: theme.palette.secondary.main,
      },
      clear: {
        background: 'transparent',
        border: 0,
        color: theme.palette.darkGrey.color,
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: 500,
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: 6,
        '&:hover': {
          background: theme.palette.red.bg,
          color: theme.palette.status.error,
        },
      },
    }),
  { name: 'FormNote' }
);

export const FormNote = ({
  name,
  title,
  icon,
  placeholder,
  hint,
  maxLength,
  minHeight = 70,
  autoFocus = false,
  onClear,
  className,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [{ value }, , helpers] = useField(name);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  const length = (value || '').length;
  const hasValue = !!(value && value.trim().length);

  return (
    <div className={[classes.note, className].filter(Boolean).join(' ')}>
      {(icon || title || maxLength) && (
        <div className={classes.head}>
          {icon ? <span className={classes.icn}>{icon}</span> : null}
          {title ? <span className={classes.title}>{title}</span> : null}
          {maxLength ? (
            <span className={classes.count}>
              {length}/{maxLength}
            </span>
          ) : null}
        </div>
      )}
      <textarea
        ref={textareaRef}
        className={classes.input}
        style={{ minHeight }}
        value={value || ''}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => helpers.setValue(e.target.value)}
      />
      {(hint || hasValue) && (
        <div className={classes.foot}>
          <span className={classes.hint}>{hint}</span>
          {hasValue && (
            <button
              type="button"
              className={classes.clear}
              onClick={() => {
                helpers.setValue('');
                onClear?.();
              }}
            >
              {formatMessage({ id: 'clear' })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
